import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Card, Col, Flex, Row, Skeleton, Space, Tag, Typography, Grid } from 'antd';
import { ToolFilled, UserOutlined, CalendarOutlined, DatabaseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { get_load_info, load_files_process } from '../../redux/actions/selfActions';

// Utils
import { get_load_source_desc, get_load_status_desc, get_load_type_desc } from '../../utils/get_status_desc';
import { get_load_status_color } from '../../utils/get_color_status';

const { useBreakpoint } = Grid;

const WizardLoadInfoView = (props) => {
	const { messageApi, loadId } = props;
	
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const screens = useBreakpoint();
	
	const { load_info_loading, load_info } = useSelector(state => state.self);
	
	const [isMobile, setIsMobile] = useState(false);
	
	useEffect(() => {
		setIsMobile(!screens.md);
	}, [screens]);
	
	useEffect(() => {
		if (loadId) {
			dispatch(get_load_info(loadId));
		}
	}, [loadId, dispatch]);
	
	const InfoItem = ({ icon, label, value, color, isLast }) => (
		<div style={{ padding: '8px 0', borderBottom: isLast ? 'none' : '1px solid #f0f0f0' }}>
			<Flex align="center" gap={8}>
				{icon}
				<Typography.Text strong style={{ minWidth: '100px' }}>{label}:</Typography.Text>
				<div style={{ color }}>{value}</div>
			</Flex>
		</div>
	);
	
	return (
		<Flex justify='center'>
			<div style={{ width: '100%', maxWidth: 1000 }}>
				<Skeleton loading={load_info_loading} active>
					<Row gutter={[24, 24]}>
						{/* Load Header */}
						<Col span={24}>
							<div style={{ textAlign: 'center', marginBottom: '24px' }}>
								<Typography.Title 
									level={3} 
									style={{ margin: '8px 0' }}
									copyable={{ 
										text: load_info?._id?.$oid, 
										tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] 
									}}
								>
									{load_info?.name}
								</Typography.Title>
								<Typography.Text type='secondary' style={{ fontSize: '16px' }}>
									{load_info?.description}
								</Typography.Text>
							</div>
						</Col>
						
						{/* Load Information */}
						<Col xs={24} md={12}>
							<Card 
								size="small" 
								title="Información de la Carga"
								style={{ height: '100%' }}
								headStyle={{ backgroundColor: '#f5f5f5' }}
							>
								<InfoItem
									icon={<DatabaseOutlined style={{ color: '#52c41a' }} />}
									label={t('routesTable.headers.status')}
									value={<Tag color={get_load_status_color(load_info?.status)}>{get_load_status_desc(load_info?.status)}</Tag>}
								/>
								<InfoItem
									icon={<DatabaseOutlined style={{ color: '#722ed1' }} />}
									label={t('loadStatusDetails.card.source')}
									value={get_load_source_desc(load_info?.source)}
								/>
								<InfoItem
									icon={<DatabaseOutlined style={{ color: '#fa8c16' }} />}
									label={t('loadFileInput.loadType')}
									value={get_load_type_desc(load_info?.load_type)}
									isLast={true}
								/>
							</Card>
						</Col>
						
						{/* Date Information */}
						<Col xs={24} md={12}>
							<Card 
								size="small" 
								title="Información de Fecha"
								style={{ height: '100%' }}
								headStyle={{ backgroundColor: '#f5f5f5' }}
							>
								<InfoItem
									icon={<CalendarOutlined style={{ color: '#faad14' }} />}
									label={t('loadStatus.statusTable.date')}
									value={new Date(load_info?.date?.$date).toLocaleString(localStorage.getItem('i18nextLng'), { hour12: true })}
									isLast={true}
								/>
							</Card>
						</Col>
						
						{/* Actions */}
						<Col span={24}>
							<Card 
								size="small" 
								title="Acciones Disponibles"
								style={{ textAlign: 'center' }}
								headStyle={{ backgroundColor: '#f5f5f5' }}
							>
								{/* Process Actions */}
								<div style={{ marginBottom: '16px' }}>
									<Typography.Text strong style={{ display: 'block', marginBottom: '8px', color: '#1677ff' }}>
										Procesamiento
									</Typography.Text>
									<Space wrap size={[8, 8]} style={{ justifyContent: 'center' }}>
										<Button
											type='primary'
											style={{ backgroundColor: '#faad14', borderColor: '#faad14' }}
											icon={<ToolFilled />}
											onClick={() => dispatch(load_files_process(messageApi, loadId))}
											size={isMobile ? 'small' : 'default'}
										>
											{t('loadStatusDetails.process.button')}
										</Button>
									</Space>
								</div>
							</Card>
						</Col>
					</Row>
				</Skeleton>
			</div>
		</Flex>
	)
}

WizardLoadInfoView.propTypes = {
	messageApi: PropTypes.object.isRequired,
	loadId: PropTypes.string
}

export default WizardLoadInfoView;